import { Color, color, interpolateColors } from '../visual-analytics/helpers/colors';

interface TpTheme {
  id: string;
  name: string;

  colors: {
    inverted: boolean;
    text: Color;
    background: Color;
    background2: Color; // in a white background theme, a slight gray background
    supportPrim: Color; //primary support color, used for most color accents
    supportSec: Color; //secondary support color
    chartData: Color; // used for data visualization in charts
    highlight: Color; //highlight support color
    errorBackground: Color; //primary error color
    errorBackground2: Color; //primary error color for hovering
    errorForeground: Color;
  };
}

export const themeBioLizardDefault: TpTheme = {
  id: 'BioLizardDefault',
  name: 'BioLizard - white background',
  colors: {
    inverted: false,
    text: color(0, 0, 0),
    background: color(255, 255, 255),
    background2: color(240, 240, 240),
    supportPrim: color(0, 169, 143),
    supportSec: color(49, 100, 130),
    highlight: color(255, 140, 0),
    chartData: color(49, 100, 130).lighten(0.4),
    errorBackground: color(195, 10, 25),
    errorBackground2: color(169, 0, 37),
    errorForeground: color(255, 255, 255),
  },
};

export const themeBioLizardDark: TpTheme = {
  id: 'BioLizardDark',
  name: 'BioLizard - dark background',
  colors: {
    inverted: true,
    text: color(230, 230, 230),
    background: color(30, 34, 55).darken(0.1),
    background2: color(30, 34, 55).lighten(0.1),
    supportPrim: color(0, 169, 143).darken(0.05),
    supportSec: color(49, 100, 130).darken(0.15),
    highlight: color(255, 170, 0),
    chartData: color(49, 100, 130).lighten(0.2),
    errorBackground: color(246, 155, 44),
    errorBackground2: color(171, 107, 29),
    errorForeground: color(30, 34, 55).darken(0.1),
  },
};

export const themePOCDark: TpTheme = {
  id: 'POCDark',
  name: 'POC - dark background',
  colors: {
    inverted: true,
    text: color(255, 220, 170),
    background: color(0, 0, 40),
    background2: color(30, 20, 60),
    supportPrim: color(180, 0, 90),
    supportSec: color(0, 40, 160),
    highlight: color(255, 255, 0),
    chartData: color(0, 40, 160).lighten(0.3),
    errorBackground: color(180, 0, 90),
    errorBackground2: color(122, 0, 61),
    errorForeground: color(255, 255, 255),
  },
};

const allThemes = [themeBioLizardDefault, themeBioLizardDark, themePOCDark];
let currentThemeIndex = 0;

export let currentTheme: TpTheme;

export function applyTheme(themeDef: TpTheme) {
  currentTheme = themeDef;

  function setColor(varName: string, color: Color) {
    document.documentElement.style.setProperty(varName, color.toCanvasString());
  }

  // NOTE: changing the color scheme in case of a dark theme is necessary because some elements are automatically styled by the browser, such as input:-internal-autofill-selected
  document.documentElement.style.setProperty('--color-scheme', themeDef.colors.inverted ? 'dark' : 'light');

  setColor('--light-color', themeDef.colors.background);
  setColor('--lightgray-background', themeDef.colors.background2);
  setColor('--base-color', themeDef.colors.supportPrim);
  setColor('--text-color', themeDef.colors.text);

  setColor('--error-background', themeDef.colors.errorBackground);
  setColor('--error-background-darker', themeDef.colors.errorBackground2);
  setColor('--error-foreground', themeDef.colors.errorForeground);

  setColor('--base-color-background', themeDef.colors.supportPrim);
  setColor('--base-color-foreground', color(255, 255, 255));

  setColor('--prim2-color', themeDef.colors.supportSec);

  setColor('--prim4-color', themeDef.colors.highlight);

  if (!themeDef.colors.inverted) {
    setColor('--base-color-darker1', themeDef.colors.supportPrim.darken(0.3));
    setColor('--base-color-darker2', themeDef.colors.supportPrim.darken(0.3));
    setColor('--base-color-lighter1', themeDef.colors.supportPrim.lighten(0.3));
    setColor('--base-color-lighter2', themeDef.colors.supportPrim.lighten(0.6));
    setColor('--base-color-lighter3', themeDef.colors.supportPrim.lighten(0.75));
    setColor('--prim2-color-darker1', themeDef.colors.supportSec.darken(0.3));
    setColor('--prim2-color-darker2', themeDef.colors.supportSec.darken(0.6));
    setColor('--prim2-color-lighter1', themeDef.colors.supportSec.lighten(0.3));
    setColor('--prim2-color-lighter2', themeDef.colors.supportSec.lighten(0.6));
  } else {
    setColor('--base-color-darker2', themeDef.colors.supportPrim.lighten(0.6));
    setColor('--base-color-darker1', themeDef.colors.supportPrim.lighten(0.3));
    setColor('--base-color-lighter1', themeDef.colors.supportPrim.darken(0.2));
    setColor('--base-color-lighter2', themeDef.colors.supportPrim.darken(0.4));
    setColor('--base-color-lighter3', themeDef.colors.supportPrim.darken(0.6));
    setColor('--prim2-color-darker1', themeDef.colors.supportSec.lighten(0.2));
    setColor('--prim2-color-darker2', themeDef.colors.supportSec.lighten(0.4));
    setColor('--prim2-color-lighter1', themeDef.colors.supportSec.darken(0.2));
    setColor('--prim2-color-lighter2', themeDef.colors.supportSec.darken(0.4));
  }

  const black = !themeDef.colors.inverted ? color(0, 0, 0) : color(255, 255, 255);
  setColor('--blackOP50', black.opacity(0.5));
  setColor('--blackOP30', black.opacity(0.3));
  setColor('--blackOP20', black.opacity(0.2));
  setColor('--blackOP15', black.opacity(0.15));
  setColor('--blackOP10', black.opacity(0.1));
  setColor('--blackOP05', black.opacity(0.05));

  setColor('--gray50', interpolateColors(themeDef.colors.background, themeDef.colors.text, 0.5));
  setColor('--gray75', interpolateColors(themeDef.colors.background, themeDef.colors.text, 0.75));
  setColor('--gray90', interpolateColors(themeDef.colors.background, themeDef.colors.text, 0.9));

  setColor('--highlightTextBg', interpolateColors(themeDef.colors.background, themeDef.colors.highlight, 0.5));

  setColor('--foreground-support-prim', color(255, 255, 255));
}

export function applyDefaultTheme() {
  currentThemeIndex = 0;
  applyTheme(allThemes[currentThemeIndex]);
}

export function changeTheme() {
  currentThemeIndex = (currentThemeIndex + 1) % allThemes.length;
  applyTheme(allThemes[currentThemeIndex]);
}

export function getThemedGrayFraction(lightness: number) {
  return interpolateColors(currentTheme.colors.background, currentTheme.colors.text, lightness);
}

export function themedLighen(color: Color, fraction: number) {
  if (!currentTheme.colors.inverted) return color.lighten(fraction);
  else return color.darken(fraction);
}

export function getStandardDataColor() {
  return currentTheme.colors.chartData;
}

export function getSelectionHighlightColor() {
  return currentTheme.colors.highlight;
}
